// Prospection Table V2 - improved design with status dropdown and notes
import React, { useState, useEffect, useRef } from "react";
import {
    EnvelopeIcon,
    PhoneIcon,
    StarIcon,
    ChatBubbleLeftIcon,
    XMarkIcon,
    GlobeAltIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { FaFacebook, FaInstagram, FaTwitter, FaTiktok, FaYoutube, FaLinkedinIn, FaUber } from "react-icons/fa";
import { ILead, LeadStatus } from "../../interfaces/prospectionList";
import { EstablishmentLogo } from "../franchise/EstablishmentLogo";
import { getFounderEstablishmentCounts } from "../../interfaces/establishments";
import { FounderCounts, founderCountLabel } from "../../lib/founderCounts";
import { useNavigate } from "react-router-dom";

// Status → design-system tone (see DESIGN_SYSTEM.md §1.1). Shared with ProspectionView.tsx.
export const STATUS_TONE: Record<LeadStatus, "neutral" | "success" | "warning" | "danger" | "info"> = {
    "to_contact": "neutral",
    "linkedin_pending": "info",
    "nrp1": "warning",
    "nrp2": "warning",
    "nrp3": "danger",
    "contacted": "success",
    "suivi": "info",
    "to_followup": "warning",
    "ignored": "neutral",
};

const STATUS_LABELS: Record<LeadStatus, string> = {
    "to_contact": "À contacter",
    "linkedin_pending": "Invit. LinkedIn",
    "nrp1": "NRP 1",
    "nrp2": "NRP 2",
    "nrp3": "NRP 3",
    "contacted": "Contacté",
    "suivi": "Suivi",
    "to_followup": "À relancer",
    "ignored": "Ignoré",
};

const TONE_TEXT: Record<string, string> = {
    neutral: "text-ink-muted",
    success: "text-success",
    warning: "text-warning",
    danger: "text-danger",
    info: "text-info",
};
const TONE_DOT: Record<string, string> = {
    neutral: "bg-ink-faint",
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
    info: "bg-info",
};

// Status badge colors (derived from tone)
const statusColors: Record<LeadStatus, { text: string; dot: string; label: string }> = (
    Object.keys(STATUS_TONE) as LeadStatus[]
).reduce((acc, key) => {
    const tone = STATUS_TONE[key];
    acc[key] = { text: TONE_TEXT[tone], dot: TONE_DOT[tone], label: STATUS_LABELS[key] };
    return acc;
}, {} as Record<LeadStatus, { text: string; dot: string; label: string }>);

// Helper: extract enrichment data from a person's enrichments array
function getEnrichment(enrichments: any[], type: string) {
    return enrichments?.find(
        (e: any) => e.type === type && (e.status === "done" || e.status === "approved")
    );
}

// Capitalize first letter of each word in a city name
function formatCity(city: string): string {
    return city.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// Format name: first letter uppercase, rest lowercase, for each word
function formatName(name: string): string {
    return name.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// Compute age from "MM-YYYY" birthdate string
function computeAgeFromBirthdate(birthdateStr: string): number | null {
    const parts = birthdateStr.split("-");
    if (parts.length !== 2) return null;
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);
    if (isNaN(month) || isNaN(year)) return null;
    const now = new Date();
    let age = now.getFullYear() - year;
    if (now.getMonth() + 1 < month) age--;
    return age;
}

interface StatusDropdownProps {
    currentStatus: LeadStatus;
    onStatusChange: (status: LeadStatus) => void;
}

function StatusDropdown({ currentStatus, onStatusChange }: StatusDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const currentColor = statusColors[currentStatus];

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className="flex items-center justify-between gap-2 w-full min-w-[140px] px-3 py-2 bg-surface border border-border rounded-lg hover:border-border-strong transition-colors duration-150 group"
            >
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${currentColor.text}`}>
                    <span className={`w-2 h-2 rounded-full ${currentColor.dot}`}></span>
                    {currentColor.label}
                </span>
                <ChevronDownIcon className={`w-4 h-4 text-ink-faint transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="absolute z-50 mt-2 w-full bg-surface border border-border rounded-2xl shadow-float py-1">
                    {(Object.keys(statusColors) as LeadStatus[]).map((status) => {
                        const color = statusColors[status];
                        const isSelected = status === currentStatus;
                        return (
                            <button
                                key={status}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onStatusChange(status);
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors duration-150 ${
                                    isSelected ? "bg-surface-sunken" : "hover:bg-surface-sunken"
                                }`}
                            >
                                <span className={`w-2 h-2 rounded-full ${color.dot}`}></span>
                                <span className={`${color.text} ${isSelected ? "font-semibold" : "font-medium"}`}>
                                    {color.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

interface NotesModalProps {
    isOpen: boolean;
    notes: string;
    onClose: () => void;
    onSave: (notes: string) => void;
}

function NotesModal({ isOpen, notes, onClose, onSave }: NotesModalProps) {
    const [editedNotes, setEditedNotes] = useState(notes);

    useEffect(() => {
        setEditedNotes(notes);
    }, [notes]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-surface rounded-2xl shadow-float w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                    <h3 className="text-lg font-semibold text-ink">Notes</h3>
                    <button
                        onClick={onClose}
                        className="text-ink-faint hover:text-ink-muted transition-colors duration-150"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <div className="p-6">
                    <textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        placeholder="Ajouter des notes sur ce prospect..."
                        className="w-full h-48 px-4 py-3 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent resize-none"
                    />
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-ink-muted hover:text-ink transition-colors duration-150"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => {
                            onSave(editedNotes);
                            onClose();
                        }}
                        className="px-4 py-2 text-sm bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors duration-150"
                    >
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

interface ProspectionTableV2Props {
    leads: ILead[];
    onLeadUpdate: (leadId: string, updates: Partial<ILead>) => void;
}

export default function ProspectionTableV2({ leads, onLeadUpdate }: ProspectionTableV2Props) {
    const navigate = useNavigate();
    const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
    const [hoveredLinkedin, setHoveredLinkedin] = useState<string | null>(null);
    const [hoveredPhone, setHoveredPhone] = useState<string | null>(null);
    const [hoveredEmail, setHoveredEmail] = useState<string | null>(null);
    const [showNotesModal, setShowNotesModal] = useState<string | null>(null);

    const phonePopupRef = useRef<HTMLDivElement>(null);
    const linkedinPopupRef = useRef<HTMLDivElement>(null);
    const emailPopupRef = useRef<HTMLDivElement>(null);

    // Close popups on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (phonePopupRef.current && !phonePopupRef.current.contains(event.target as Node)) setHoveredPhone(null);
            if (linkedinPopupRef.current && !linkedinPopupRef.current.contains(event.target as Node)) setHoveredLinkedin(null);
            if (emailPopupRef.current && !emailPopupRef.current.contains(event.target as Node)) setHoveredEmail(null);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleManualSearch = (e: React.MouseEvent, name: string) => {
        e.stopPropagation();
        window.open(`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(name)}`, "_blank");
    };

    const handleOpenLinkedin = (e: React.MouseEvent, url: string) => {
        e.stopPropagation();
        window.open(url, "_blank");
    };

    const handleCopyPhone = async (e: React.MouseEvent, phone: string) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(phone); } catch { /* ignore */ }
    };

    const handleCopyEmail = async (e: React.MouseEvent, email: string) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(email); } catch { /* ignore */ }
    };

    const toggleSelectAll = () => {
        if (selectedLeads.size === leads.length) {
            setSelectedLeads(new Set());
        } else {
            setSelectedLeads(new Set(leads.map(l => l.establishmentId)));
        }
    };

    const toggleSelectLead = (id: string) => {
        setSelectedLeads(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const [personNameCounts, setPersonNameCounts] = useState<Record<string, FounderCounts>>({});

    useEffect(() => {
        const uniqueIds = [...new Set(
            leads.map(lead => (lead.establishment?.peoples?.[0] as any)?._id).filter(Boolean) as string[]
        )];
        if (uniqueIds.length === 0) return;
        getFounderEstablishmentCounts(uniqueIds)
            .then(setPersonNameCounts)
            .catch(() => { /* ignore */ });
    }, [leads]);

    const detectSocialMedia = (url: string) => {
        if (!url) return null;
        const l = url.toLowerCase();
        if (l.includes("facebook.com") || l.includes("fb.com")) return { type: "facebook", color: "#1877f2" };
        if (l.includes("instagram.com")) return { type: "instagram", color: "#E4405F" };
        if (l.includes("twitter.com") || l.includes("x.com")) return { type: "twitter", color: "#1DA1F2" };
        if (l.includes("tiktok.com")) return { type: "tiktok", color: "#000000" };
        if (l.includes("youtube.com")) return { type: "youtube", color: "#FF0000" };
        if (l.includes("ubereats.com")) return { type: "ubereats", color: "#06C167" };
        return { type: "website", color: "#6B7280" };
    };

    const getSocialMediaIcon = (type: string) => {
        const p = { className: "w-5 h-5" };
        switch (type) {
            case "facebook": return <FaFacebook {...p} />;
            case "instagram": return <FaInstagram {...p} />;
            case "twitter": return <FaTwitter {...p} />;
            case "tiktok": return <FaTiktok {...p} />;
            case "youtube": return <FaYoutube {...p} />;
            case "linkedin": return <FaLinkedinIn {...p} />;
            case "ubereats": return <FaUber {...p} />;
            default: return <GlobeAltIcon {...p} />;
        }
    };

    return (
        <div className="bg-surface border-t border-border">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-surface-sunken">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left w-12">
                                <input
                                    type="checkbox"
                                    className="rounded-sm border-border text-accent focus:ring-accent h-4 w-4"
                                    checked={leads.length > 0 && selectedLeads.size === leads.length}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Contact</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Coordonnées</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Liens</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Entreprise</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Statut</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Notes</th>
                        </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-border">
                        {leads.map((lead) => {
                            const est = lead.establishment;
                            if (!est) return null;
                            
                            const isSelected = selectedLeads.has(lead.establishmentId);
                            const primaryPerson = est.peoples?.[0];
                            const enrichments: any[] = (primaryPerson as any)?.enrichments || [];
                            const personName: string | undefined = (primaryPerson as any)?.name;
                            const phoneEnrichment = getEnrichment(enrichments, "phone");
                            const emailEnrichment = getEnrichment(enrichments, "email_company");
                            const linkedinEnrichment = getEnrichment(enrichments, "linkedin");
                            const birthdateEnrichment = getEnrichment(enrichments, "birthdate");
                            const founderAge = birthdateEnrichment?.data
                                ? computeAgeFromBirthdate(birthdateEnrichment.data)
                                : null;
                            const phone: string | undefined = est.phone || phoneEnrichment?.data?.phone;
                            const email: string | undefined = est.email || emailEnrichment?.data?.email;
                            const linkedin: string | undefined = linkedinEnrichment?.data?.url;
                            const instagram: string | undefined = est.social_networks?.find((sn: any) => sn.platform === 'instagram')?.url;
                            const googleRating = est.ratings?.source_summary?.[0];

                            return (
                                <tr
                                    key={lead.establishmentId}
                                    className={`transition-colors duration-150 group ${isSelected ? "bg-info-subtle/40" : ""}`}
                                >
                                    {/* Checkbox */}
                                    <td className="px-6 py-4 whitespace-nowrap align-middle" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="rounded-sm border-border text-accent focus:ring-accent h-4 w-4"
                                            checked={isSelected}
                                            onChange={() => toggleSelectLead(lead.establishmentId)}
                                        />
                                    </td>

                                    {/* Contact */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex flex-col">
                                            {personName ? (
                                                <>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-sm font-medium text-ink">{formatName(personName)}</span>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/signals/founder?founderName=${encodeURIComponent(personName)}`);
                                                        }}
                                                        className="text-xs text-ink-muted mt-0.5 italic text-left hover:text-accent transition-colors duration-150"
                                                    >
                                                        {/* Joined rather than concatenated: the count is null while it loads,
                                                            and a hardcoded separator would leave a dangling "46 ans - ". */}
                                                        {[
                                                            founderAge !== null ? `${founderAge} ans` : null,
                                                            founderCountLabel(personNameCounts[(primaryPerson as any)?._id]),
                                                        ].filter(Boolean).join(" - ")}
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="text-sm text-ink-faint italic">Enrichissement en cours...</span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Coordinates */}
                                    <td className="px-6 py-4 align-middle whitespace-nowrap">
                                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                                            {/* Phone */}
                                            <div className="relative">
                                                <button
                                                    className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
                                                        phone ? "text-ink-muted hover:text-ink hover:bg-surface-sunken" : "opacity-20 cursor-default"
                                                    }`}
                                                    disabled={!phone}
                                                    onClick={(e) => { e.stopPropagation(); setHoveredPhone(hoveredPhone === lead.establishmentId ? null : lead.establishmentId); }}
                                                >
                                                    <PhoneIcon className="w-5 h-5" />
                                                </button>
                                                {phone && (
                                                    <>
                                                        <span className="absolute -top-1 -right-1 bg-surface-sunken text-ink-muted text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">1</span>
                                                        {hoveredPhone === lead.establishmentId && (
                                                            <div ref={phonePopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                                                <div className="bg-surface border border-border rounded-lg shadow-float p-4">
                                                                    <p className="text-sm font-medium text-ink mb-3">Numéro de téléphone</p>
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <div className="text-sm text-ink">{phone}</div>
                                                                        <button onClick={(e) => handleCopyPhone(e, phone)} className="px-2 py-1 text-xs bg-surface-sunken hover:bg-border rounded-sm transition-colors duration-150">Copier</button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Mobile placeholder */}
                                            <div className="relative">
                                                <button className="flex items-center justify-center w-9 h-9 rounded-lg opacity-20 cursor-default" disabled>
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                    </svg>
                                                </button>
                                            </div>

                                            {/* Email */}
                                            <div className="relative">
                                                <button
                                                    className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
                                                        email ? "text-ink-muted hover:text-ink hover:bg-surface-sunken" : "opacity-20 cursor-default"
                                                    }`}
                                                    disabled={!email}
                                                    onClick={(e) => { e.stopPropagation(); setHoveredEmail(hoveredEmail === lead.establishmentId ? null : lead.establishmentId); }}
                                                >
                                                    <EnvelopeIcon className="w-5 h-5" />
                                                </button>
                                                {email && (
                                                    <>
                                                        <span className="absolute -top-1 -right-1 bg-surface-sunken text-ink-muted text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">1</span>
                                                        {hoveredEmail === lead.establishmentId && (
                                                            <div ref={emailPopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                                                <div className="bg-surface border border-border rounded-lg shadow-float p-4">
                                                                    <p className="text-sm font-medium text-ink mb-3">Email disponible</p>
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <div className="text-sm text-ink truncate">{email}</div>
                                                                        <button onClick={(e) => handleCopyEmail(e, email)} className="px-2 py-1 text-xs bg-surface-sunken hover:bg-border rounded-sm transition-colors duration-150 flex-shrink-0">Copier</button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Links */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center gap-3">
                                            {/* LinkedIn */}
                                            <div className="relative flex items-center">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setHoveredLinkedin(hoveredLinkedin === lead.establishmentId ? null : lead.establishmentId); }}
                                                    className={`transition-colors duration-150 ${linkedin ? "text-info hover:text-info/80" : "text-ink-faint hover:text-ink-muted"}`}
                                                >
                                                    <FaLinkedinIn className="w-5 h-5" />
                                                </button>
                                                {hoveredLinkedin === lead.establishmentId && (
                                                    <div ref={linkedinPopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                                        <div className="bg-surface border border-border rounded-lg shadow-float p-4">
                                                            {linkedin ? (
                                                                <>
                                                                    <p className="text-sm text-ink-muted mb-3">Profil LinkedIn trouvé pour {personName}</p>
                                                                    <button onClick={(e) => handleOpenLinkedin(e, linkedin)} className="w-full bg-[#0077b5] text-white px-3 py-2 rounded-lg hover:bg-[#006399] transition-colors duration-150 text-sm font-medium">
                                                                        Ouvrir le profil LinkedIn
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <p className="text-sm text-ink-muted mb-3">Aucun profil LinkedIn trouvé pour {personName || est.name}.</p>
                                                                    <button onClick={(e) => handleManualSearch(e, personName || est.name)} className="w-full bg-accent text-white px-3 py-2 rounded-lg hover:bg-accent-hover transition-colors duration-150 text-sm font-medium">
                                                                        Rechercher manuellement
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Instagram */}
                                            {instagram && (
                                                <a
                                                    href={instagram}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-pink-500 hover:text-pink-600 transition-colors duration-150"
                                                    title={`Ouvrir Instagram`}
                                                >
                                                    <FaInstagram className="w-5 h-5" />
                                                </a>
                                            )}

                                            {/* Website */}
                                            {est.website_url && (
                                                <a
                                                    href={est.website_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className={`transition-colors duration-150 ${detectSocialMedia(est.website_url || "")?.type === "website" ? "text-info hover:text-info/80" : "hover:opacity-80"}`}
                                                    title={`Visit ${est.website_url}`}
                                                    style={detectSocialMedia(est.website_url || "")?.type !== "website" ? { color: detectSocialMedia(est.website_url || "")?.color } : undefined}
                                                >
                                                    {getSocialMediaIcon(detectSocialMedia(est.website_url || "")?.type || "website")}
                                                </a>
                                            )}
                                        </div>
                                    </td>

                                    {/* Company */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-surface border border-border rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                <EstablishmentLogo name={est.name || ""} franchise={(est as any).franchise} />
                                            </div>
                                            <div className="flex flex-col">
                                                <a
                                                    href={`/establishments/${est._id}`}
                                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(`/establishments/${est._id}`); }}
                                                    className="text-sm font-semibold text-ink hover:text-accent hover:underline text-left transition-colors duration-150"
                                                >
                                                    {est.name}
                                                </a>
                                                {est.address?.city && (
                                                    <span className="text-xs text-ink-muted">{formatCity(est.address.city)}</span>
                                                )}
                                                {googleRating && (
                                                    <a
                                                        href={googleRating.url || undefined}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`flex items-center gap-1 text-xs text-ink-muted hover:text-ink transition-colors duration-150 hover:underline${!googleRating.url ? " pointer-events-none" : ""}`}
                                                    >
                                                        <StarIcon className="w-3.5 h-3.5 text-warning fill-current" />
                                                        <span className="font-medium text-ink-muted">{googleRating.rating_avg?.toFixed(1) || "—"}</span>
                                                        <span className="text-ink-faint">({googleRating.rating_count || 0} avis)</span>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Status */}
                                    <td className="px-6 py-4 align-middle">
                                        <StatusDropdown
                                            currentStatus={lead.status}
                                            onStatusChange={(newStatus) => onLeadUpdate(lead.establishmentId, { status: newStatus })}
                                        />
                                    </td>

                                    {/* Notes */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="min-w-[160px]">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowNotesModal(lead.establishmentId);
                                                }}
                                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors duration-150 text-sm ${
                                                    lead.notes
                                                        ? "bg-info-subtle border-info/30 text-info hover:border-info/50"
                                                        : "bg-surface border-border text-ink-muted hover:border-border-strong"
                                                }`}
                                            >
                                                <ChatBubbleLeftIcon className="w-4 h-4 flex-shrink-0" />
                                                <span className="truncate text-xs font-medium">
                                                    {lead.notes ? lead.notes : "Ajouter une note"}
                                                </span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Notes Modal */}
            {showNotesModal && (
                <NotesModal
                    isOpen={true}
                    notes={leads.find(l => l.establishmentId === showNotesModal)?.notes || ""}
                    onClose={() => setShowNotesModal(null)}
                    onSave={(notes) => {
                        onLeadUpdate(showNotesModal, { notes });
                        setShowNotesModal(null);
                    }}
                />
            )}
        </div>
    );
}
